import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, PageProps, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import type { DocumentCallback } from "react-pdf/src/shared/types.js";
import { useDebounceCallback, useResizeObserver } from "usehooks-ts";
import { Button } from "../../primitives";
import { cn } from "../../utils/classes";

// The worker must match the PDF.js API bundled by react-pdf exactly.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PDFFile = string | File;

type Size = {
  width?: number;
};

const PDFPage = ({
  containerWidth,
  ...props
}: PageProps & {
  containerWidth?: number;
}) => {
  const [pageWidth, setPageWidth] = useState(0);

  const pageWidthScale = containerWidth
    ? containerWidth / pageWidth
    : undefined;
  const pageScale = pageWidthScale || 1;

  return (
    <Page
      onLoadSuccess={(page) => {
        setPageWidth(page.originalWidth);
      }}
      {...props}
      scale={pageScale}
    />
  );
};

export type PDFViewerProps = {
  file: PDFFile;
  onSelect?: (text: string, e: MouseEvent) => void;
  className?: string;
  enableZoom?: boolean;
};

const PDFViewer = forwardRef<HTMLDivElement, PDFViewerProps>(
  ({ file, onSelect, className, enableZoom }, ref) => {
    const mFile = useMemo(() => file, [file]);
    const [numPages, setNumPages] = useState<number>();
    const [zoom, setZoom] = useState<number>(1);
    const internalRef = useRef<HTMLDivElement | null>(null);
    const [{ width: containerWidth }, setSize] =
      useState<Size>({
        width: undefined,
      });

    // sets both internal and forwarded refs
    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        internalRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref]
    );

    const onResize = useDebounceCallback(
      (s: Size) =>
        setSize({
          width: s.width ? s.width - 64 : 0,
        }),
      200,
    );

    useResizeObserver({
      // usehooks-ts's `ref` option still expects the pre-React-19 non-nullable
      // `RefObject<T>` shape; internalRef is `HTMLDivElement | null` per
      // React 19 convention, so cast at this boundary only.
      ref: internalRef as React.RefObject<HTMLDivElement>,
      onResize,
      box: "border-box",
    });

    const onDocumentLoadSuccess = useCallback(({
      numPages: nextNumPages,
    }: DocumentCallback): void => {
      setNumPages(nextNumPages);
    }, []);

    useEffect(() => {
      if (!onSelect) return;

      const handleMouseUp = (e: MouseEvent) => {
        const selection = window.getSelection();
        const text = selection?.toString().trim();

        if (text && text.length > 0 && selection?.rangeCount) {
          const range = selection.getRangeAt(0);
          const rects = range.getClientRects();

          // Get the last rect (end of selection)
          const lastRect = rects[rects.length - 1];

          // Create a new mouse event with the position at the end of the selection
          const endEvent = new MouseEvent("mouseup", {
            clientX: lastRect.right,
            clientY: lastRect.bottom,
            bubbles: true,
          });

          onSelect(text, endEvent);
        }
      };

      const container = internalRef.current;
      if (container) {
        container.addEventListener("mouseup", handleMouseUp);
      }

      return () => {
        if (container) {
          container.removeEventListener("mouseup", handleMouseUp);
        }
      };
    }, [onSelect]);

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.25));
    const handleResetZoom = () => setZoom(1);

    const documentContent = useMemo(
      () => (
        <Document
          file={mFile}
          onLoadSuccess={onDocumentLoadSuccess}
          className="flex w-max min-w-full flex-col items-start gap-8"
        >
          {Array.from(new Array(numPages), (_el, index) => (
            <PDFPage
              className="rounded-sm overflow-hidden"
              containerWidth={containerWidth}
              key={`page_${index + 1}`}
              pageNumber={index + 1}
            />
          ))}
        </Document>
      ),
      [mFile, numPages, containerWidth, onDocumentLoadSuccess],
    );

    return (
      <div className={cn("relative h-full", className)}>
        {enableZoom && (
          <div className="absolute top-4 right-4 z-10 flex gap-2 bg-white rounded-sm shadow-lg p-2 border border-gray-200">
            <Button
              onClick={handleZoomOut}
              className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
              disabled={zoom <= 0.25}
              size="sm"
            >
              -
            </Button>
            <Button
              onClick={handleResetZoom}
              className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
              size="sm"
            >
              {Math.round(zoom * 100)}%
            </Button>
            <Button
              onClick={handleZoomIn}
              className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
              disabled={zoom >= 3}
              size="sm"
            >
              +
            </Button>
          </div>
        )}
        <div className="overflow-auto h-full p-8" ref={setRefs}>
          <div className="flex w-max min-w-full flex-col items-start gap-8">
            <div style={{ zoom }}>
              {documentContent}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default PDFViewer;
